import { useMemo } from 'react';
import { useCodesHierarchy } from './useCodesSidebar';
import { useCodeBarCollapseState } from './useCollapseState';
import { useCodesWithCollapse } from './useCodesWithCollapse';

/**
 * Recursively finds a code by ID in a hierarchical structure
 * @param {Array} codes - Hierarchical array of codes
 * @param {string|number} codeId - ID of code to find
 * @returns {Object|null} The found code object or null
 */
function findCodeById(codes, codeId) {
  if (!codes || !codeId) return null;

  for (const code of codes) {
    if (code.id === codeId) {
      return code;
    }
    if (code.children && code.children.length > 0) {
      const found = findCodeById(code.children, codeId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Flattens a code and its visible children into a flat array
 * Respects collapse state - doesn't include children of collapsed codes
 * @param {Object} code - The code object with children
 * @param {number} level - Current level for indentation
 * @returns {Array} Flat array of visible codes
 */
function flattenVisibleChildren(code, level = 0) {
  if (!code) return [];

  const result = [{ ...code, level }];

  // If this code is collapsed, don't include its children
  if (code.collapsed || !code.children) {
    return result;
  }

  // Recursively add visible children
  for (const child of code.children) {
    result.push(...flattenVisibleChildren(child, level + 1));
  }

  return result;
}

/**
 * Hook that provides the visible children for a dragged code
 * Uses hierarchical data with collapse state to determine visibility
 * @param {string|number} projectId - Project ID
 * @param {string|number} codeId - ID of the code being dragged
 * @returns {Object} Object containing visible codes array and loading state
 */
export function useCodeDragChildren(projectId, codeId) {
  // Get hierarchical codes
  const codesData = useCodesHierarchy(projectId);

  // Get collapse state
  const collapseData = useCodeBarCollapseState(projectId);

  // Add collapse state to hierarchical codes
  const { codes: hierarchicalWithCollapse, isLoading, error } = useCodesWithCollapse(codesData, collapseData);

  // Find the dragged code and flatten its visible children
  const visibleCodes = useMemo(() => {
    if (!codeId || !hierarchicalWithCollapse || hierarchicalWithCollapse.length === 0) {
      return [];
    }

    // Find the code in the hierarchy
    const draggedCode = findCodeById(hierarchicalWithCollapse, codeId);
    if (!draggedCode) {
      return [];
    }

    // Flatten the code and its visible children
    return flattenVisibleChildren(draggedCode);
  }, [codeId, hierarchicalWithCollapse]);

  return {
    codes: visibleCodes,
    isLoading,
    error
  };
}